// NOTE: In order to modify the input data, we need to emit the value so that the component updates its model value. Using input.value = '...' will not work!
// arg: v-auto-focus:arg -> v-auto-focus:something = {arg: 'something', ...}
// modifiers: v-auto-focus.modifiers -> v-auto-focus.something = {modifiers: {something: true}, ...}

import _ from 'lodash'
import { VNode, DirectiveBinding } from 'vue'
import { isCharNumber } from 'src/common/helpers/validators/isCharNumber'
import { removeDigit, appendDigit } from 'src/common/helpers/generator/number.helper'

interface IState {
  model: string
  numberValue: number
  value: string
  decimal: boolean
  [key: string]: string | number | boolean
}

interface IVNode extends VNode {
  ctx: VNode & { emitsOptions: Record<string, unknown>; emit: (name: string, data: unknown) => void }
}

/* -------------------------------- Constants ------------------------------- */

// Keeps each element's state so the 'updated' hook can reformat values set from outside the directive
const states = new WeakMap<HTMLElement, IState>()

const handler = {
  get(target: IState, prop: string) {
    return target[prop]
  },
  set(obj: IState, prop: string, value: string) {
    if (prop === 'value') {
      const stringValue = value.replace('%', '')
      let numberValue = Number(stringValue)
      if (isNaN(numberValue)) numberValue = 0
      obj.numberValue = numberValue
      obj.value = obj.decimal ? numberValue.toFixed(2) : String(numberValue)
      obj.model = `${obj.value}%`
    } else {
      obj[prop] = value
    }
    return true
  },
}

/* -------------------------------- Functions ------------------------------- */

/**
 * Creates the proxy object with the definitions defined inside the 'handler' variable
 */
function createState(decimal: boolean): IState {
  return new Proxy<IState>({ decimal, numberValue: 0, value: '0', model: '0%' }, handler)
}

/**
 * Initialises the input value with 0 and returns the input element
 */
function initialiseValue(el: HTMLElement, vnode: IVNode, decimal: boolean): HTMLInputElement {
  const input = el.querySelector('.q-field__native') as HTMLInputElement
  if (input.value === '') emit(vnode, `0${decimal ? '.00' : ''}%`)
  return input
}

/**
 * Uses source element's 'update:modelValue' event to emit the value and change the element's modelValue prop value
 * (This causes the 'updated' directive hook to be triggered)
 */
function emit(vnode: IVNode, data: string | number, property = 'modelValue') {
  vnode.ctx.emit(`update:${property}`, data)
}

/**
 * Handles the logic for different key presses including number keys, alphabetical keys, and backspace
 */
function handleKeyboardLogic(e: KeyboardEvent, vnode: IVNode, state: IState) {
  const key = e.key
  const value = vnode.ctx.props?.modelValue
  let shouldUpdate = true
  if (key === 'Backspace' || isCharNumber(key)) {
    state.value = value
    let updatedValue = 0
    const precision = state.decimal ? 2 : 0
    if (key === 'Backspace') {
      state.value = state.value.slice(0, -1)
      updatedValue = removeDigit(state.numberValue, precision)
    } else updatedValue = appendDigit(state.numberValue, key, precision)
    state.value = String(updatedValue)
    if (state.numberValue > 100) shouldUpdate = false
  } else shouldUpdate = false

  if (shouldUpdate) {
    emit(vnode, state.model)
    emit(vnode, state.numberValue, 'value')
  }
  e.preventDefault()
}

function handleSelectionLogic(e: KeyboardEvent, vnode: IVNode, state: IState) {
  state.value = '0'
  e.preventDefault()
  emit(vnode, state.model)
  emit(vnode, state.numberValue, 'value')
  setTimeout(() => handleKeyboardLogic(e, vnode, state))
}

export default {
  mounted(el: HTMLElement, binding: DirectiveBinding, vnode: IVNode | any) {
    const input = initialiseValue(el, vnode, binding.modifiers.decimal ?? false)
    const state = createState(binding.modifiers.decimal ?? false)
    states.set(el, state)
    // Add a custom emit option to allow emitting the number value
    vnode.ctx.emitsOptions['update:value'] = null

    const modelValue = vnode.ctx.props?.modelValue.toString().replace('%', '')
    state.value = modelValue
    emit(vnode, state.model)
    emit(vnode, state.numberValue, 'value')

    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (input.selectionStart === input.selectionEnd) handleKeyboardLogic(e, vnode, state)
      else handleSelectionLogic(e, vnode, state)
    })
  },

  // The model can be replaced from outside (e.g. the parent regenerates its rows and the input is patched in place
  // rather than re-mounted), so any value that isn't already formatted is normalised here, not just ''
  updated(el: HTMLElement, binding: DirectiveBinding, vnode: IVNode | any) {
    const state = states.get(el) ?? createState(binding.modifiers.decimal ?? false)
    const modelValue = String(vnode.ctx.props.modelValue ?? '')
    state.value = modelValue
    if (state.model !== modelValue) {
      emit(vnode, state.model)
      emit(vnode, state.numberValue, 'value')
    }
  },

  unmounted(el: HTMLElement) {
    states.delete(el)
  },
}
